import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { DepartmentInput, EmployeeInput } from './types';

export const employeeKeys = {
  all: ['employees'] as const,
  lists: () => [...employeeKeys.all, 'list'] as const,
  list: (params: api.EmployeeListParams) => [...employeeKeys.lists(), params] as const,
  detail: (id: string | number) => [...employeeKeys.all, 'detail', id] as const,
  salary: (id: number) => [...employeeKeys.all, 'salary', id] as const,
  payslips: (id: number) => [...employeeKeys.all, 'payslips', id] as const,
};

export const departmentKeys = {
  all: ['departments'] as const,
  list: (search?: string) => [...departmentKeys.all, 'list', search ?? ''] as const,
  detail: (id: number) => ['department', id] as const,
};

/** Truthy id that isn't the `-1` "skip" sentinel used by optional lookups. */
function isResolvable(id: string | number): boolean {
  return id !== '' && id !== -1 && id !== '-1' && id != null;
}

export function useEmployees(params: api.EmployeeListParams) {
  return useQuery({
    queryKey: employeeKeys.list(params),
    queryFn: () => api.listEmployees(params),
    placeholderData: keepPreviousData,
  });
}

export function useEmployee(id: string | number) {
  return useQuery({
    queryKey: employeeKeys.detail(id),
    queryFn: () => api.getEmployee(id),
    // Resolves by public_id (string, from the URL) or integer pk (e.g. a
    // manager FK); the backend accepts either (ADR-041).
    enabled: isResolvable(id),
  });
}

export function useDepartment(id: number) {
  return useQuery({
    queryKey: departmentKeys.detail(id),
    queryFn: () => api.getDepartment(id),
    enabled: id > 0,
  });
}

export function useDepartments(search?: string) {
  return useQuery({
    queryKey: departmentKeys.list(search),
    queryFn: () => api.listDepartments(search),
  });
}

export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DepartmentInput) => api.createDepartment(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: departmentKeys.all });
      toast.success('Department created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateDepartment(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<DepartmentInput>) => api.updateDepartment(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: departmentKeys.all });
      qc.invalidateQueries({ queryKey: departmentKeys.detail(id) });
      toast.success('Department updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteDepartment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: departmentKeys.all });
      toast.success('Department deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useSalaryRecords(employeeId: number) {
  return useQuery({
    queryKey: employeeKeys.salary(employeeId),
    queryFn: () => api.listSalaryRecords(employeeId),
    enabled: Number.isInteger(employeeId) && employeeId > 0,
  });
}

export function useEmployeePayslips(employeeId: number) {
  return useQuery({
    queryKey: employeeKeys.payslips(employeeId),
    queryFn: () => api.listEmployeePayslips(employeeId),
    enabled: Number.isInteger(employeeId) && employeeId > 0,
  });
}

export function useCreateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EmployeeInput) => api.createEmployee(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: employeeKeys.lists() });
      toast.success('Employee created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useCreateSalaryRecord(employeeId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<api.SalaryRecordInput, 'employee'>) =>
      api.createSalaryRecord({ ...input, employee: employeeId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: employeeKeys.salary(employeeId) });
      toast.success('Salary record added');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateEmployee(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<EmployeeInput>) => api.updateEmployee(id, input),
    onSuccess: (employee) => {
      qc.invalidateQueries({ queryKey: employeeKeys.lists() });
      qc.setQueryData(employeeKeys.detail(id), employee);
      toast.success('Employee updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUploadEmployeePhoto(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (photo: File) => api.uploadEmployeePhoto(id, photo),
    onSuccess: (employee) => {
      qc.invalidateQueries({ queryKey: employeeKeys.lists() });
      qc.setQueryData(employeeKeys.detail(id), employee);
      qc.setQueryData(employeeKeys.detail(employee.id), employee);
      toast.success('Photo updated');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useDeleteEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteEmployee(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: employeeKeys.lists() });
      toast.success('Employee deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
